import { useCallback } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useAppSelector } from 'state/store-hooks';
import { postMessage } from 'utils/helper-functions/post-message';
import { clientClickEvent } from 'utils/custom-cashier-events/client-click-event';
import { useCashierHeaderEvents } from '@pano-project/hooks/useCashierHeaderEvents';
import {
  changePanoramicStep,
  requestPanoramicPmReselect,
} from '../state/panoramic-billing-page-actions';
import { PANORAMIC_BILLING_STEP, PANORAMIC_PAYMENT_STEP } from './usePanoramicStepNavigation';

interface UsePanoramicHeaderBackResult {
  handleBack: (forceNotifyHost?: boolean) => void;
}

export const usePanoramicHeaderBack = (): UsePanoramicHeaderBackResult => {
  const dispatch = useDispatch();

  const {
    layout,
    preselectFirst,
    preselectOnlyAvailable,
    gatewaysCount,
    retryWithApm,
    obDeclineRecovery,
    billingStep,
    isMethodSelected,
  } = useAppSelector(
    (state: any) => ({
      layout: state.initTheme?.theme_editor_settings?.customization_settings?.layout,
      preselectFirst:
        state.initTheme?.theme_editor_settings?.customization_settings
          ?.preselect_first_payment_method,
      preselectOnlyAvailable:
        state.initTheme?.theme_editor_settings?.customization_settings
          ?.preselect_only_available_method,
      gatewaysCount: state.gatewaysList?.gateways?.length ?? 0,
      retryWithApm: state.location?.recoverySettingsObject?.retry_with_apm,
      obDeclineRecovery: state.location?.recoverySettingsObject?.ob_decline_recovery,
      billingStep: state.panoramicBillingPageStep ?? PANORAMIC_PAYMENT_STEP,
      isMethodSelected: state.panoramicPmSelection?.isSelected,
    }),
    shallowEqual,
  );

  const { notifyBack } = useCashierHeaderEvents();

  const selectionStepReachable = !(
    preselectFirst ||
    (gatewaysCount === 1 && preselectOnlyAvailable) ||
    retryWithApm ||
    obDeclineRecovery
  );

  const internalBackEnabled = layout === 'panoramic' && selectionStepReachable;

  const handleBack = useCallback(
    (forceNotifyHost = false) => {
      if (!forceNotifyHost && internalBackEnabled) {
        if (billingStep === PANORAMIC_BILLING_STEP) {
          clientClickEvent('back_button');
          dispatch(changePanoramicStep(PANORAMIC_PAYMENT_STEP));
          return;
        }
        if (isMethodSelected) {
          clientClickEvent('back_button');
          dispatch(requestPanoramicPmReselect());
          return;
        }
      }
      notifyBack();
    },
    [internalBackEnabled, billingStep, isMethodSelected, dispatch, notifyBack],
  );

  return { handleBack };
};
